// Native flow-vis layer — a two-pass Line Integral Convolution (LIC) renderer in
// Three.js, replacing the blown-lift webui's iframe. Pass 1 renders the RANS slice
// mesh (per-vertex velocity `_cfvec`) into a flow texture; pass 2 re-renders the
// mesh, convolving the flow texture along the flow direction → LIC streaks colored
// by a Mach colormap.
//
// Ported from thread/blown-lift-airfoil/webui/src/highlift/lic.ts (Three 0.184).
// Three 0.174 notes: WebGLRenderTarget(w, h, options) signature is unchanged, and a
// render-target texture defaults to NoColorSpace, so the pass-1 direction/noise data
// is stored linearly (no sRGB transform corrupting the encoded flow vectors). The
// renderer's default outputColorSpace (sRGB) on the final canvas pass matches the
// webui's appearance, so no ColorManagement overrides are needed.

import { useEffect, useRef } from "react";
import * as THREE from "three";
import { LIC_Dir_VS, LIC_Dir_FS, LIC_VS, LIC_FS } from "./licShaders";
import { useHighliftTheme, type HighliftPalette } from "./useHighliftTheme";
import { FOIL_VIEW, viewCameraRect, type ViewRect } from "../geometry";

/** A Mach→RGB ramp in 0–1 floats (three.js vertex-colour space). */
type Colormap = (t: number) => [number, number, number];

/** Per-element sampled surface-pressure trace (suction/pressure sides over x/c). */
export interface SurfaceCpElement {
  element: string; // "main" | "vane" | "flap" | …
  upper: [number, number][]; // [[x/c, Cp], …] suction side, sorted by x/c
  lower: [number, number][]; // [[x/c, Cp], …] pressure side, sorted by x/c
}

/** Per-element integrated force/moment coefficients (ElemLoad: CFx aft, CFz up,
 *  CMy nose-up-positive) + optional centre-of-pressure in UI coords for the arrow
 *  anchor. Mirrors the /flow dict's optional `elementForces`. */
export interface ElementForce {
  element: string;
  CFx: number;
  CFz: number;
  CMy: number;
  xCp?: number;
  yCp?: number;
}

/** Center-span RANS slice mesh feeding the LIC renderer. */
export interface FlowMesh {
  points: number[]; // flat [x0,y0,x1,y1,…] (UI coords)
  tris: number[]; // flat [i0,j0,k0,…]
  vel: number[]; // flat [u0,v0,…]
  mach: number[];
  machRange: [number, number];
  // OPTIONAL surface-Cp + per-element forces — present ONLY when the solved slice
  // carries the airfoil surface samples (a remote rans.pipeline change). Absent →
  // honest empty Cp strip / no force arrows; never fabricated.
  // TODO(highlift-studio): lights up when the rans runner emits Cp/forces.
  surfaceCp?: SurfaceCpElement[];
  elementForces?: ElementForce[];
}

/** Blue→cyan→green→yellow→red ramp for the Mach colormap (default fallback). */
function colormap(t: number): [number, number, number] {
  t = Math.max(0, Math.min(1, t));
  const stops: Array<[number, [number, number, number]]> = [
    [0.0, [0.23, 0.3, 0.75]],
    [0.25, [0.0, 0.7, 0.9]],
    [0.5, [0.0, 0.8, 0.3]],
    [0.75, [0.95, 0.85, 0.1]],
    [1.0, [0.85, 0.15, 0.1]],
  ];
  for (let i = 1; i < stops.length; i++) {
    if (t <= stops[i]![0]) {
      const [a, ca] = stops[i - 1]!;
      const [b, cb] = stops[i]!;
      const f = (t - a) / (b - a);
      return [ca[0] + (cb[0] - ca[0]) * f, ca[1] + (cb[1] - ca[1]) * f, ca[2] + (cb[2] - ca[2]) * f];
    }
  }
  return stops[stops.length - 1]![1];
}

/**
 * Wrap the theme palette's `machColor` (0–255 ints) into the 0–1 float ramp the
 * three.js vertex colours expect, so the Mach colormap follows the light/dark
 * toggle.
 */
function paletteColormap(palette: HighliftPalette): Colormap {
  return (t: number): [number, number, number] => {
    const [r, g, b] = palette.machColor(t);
    return [r / 255, g / 255, b / 255];
  };
}

/**
 * Two-pass LIC renderer over a single static RANS slice. One instance owns a
 * WebGLRenderer, a flow render target, and two ShaderMaterials; `dispose()`
 * releases them all. Mirrors the webui LicView class.
 */
class LicView {
  private renderer: THREE.WebGLRenderer;
  private scene = new THREE.Scene();
  private camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -1, 1);
  private flowRT: THREE.WebGLRenderTarget;
  private dirMat: THREE.ShaderMaterial;
  private licMat: THREE.ShaderMaterial;
  private dummyTex: THREE.DataTexture;
  private mesh: THREE.Mesh | null = null;
  private w = 1;
  private h = 1;
  private pr = 1;
  /** Theme-aware Mach→RGB ramp (0–1). Swapped on theme flip via `setColormap`. */
  private colormap: Colormap = colormap;

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    this.renderer.setClearColor(0x000000, 0);
    this.dummyTex = new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1);
    this.dummyTex.needsUpdate = true;
    const common = { noiseTexture: { value: this.dummyTex }, textureRepeat: { value: 500 } };
    // DoubleSide: VTK triangle winding is arbitrary, so don't let backface culling
    // drop half (or all) of the slice mesh.
    this.dirMat = new THREE.ShaderMaterial({
      vertexShader: LIC_Dir_VS,
      fragmentShader: LIC_Dir_FS,
      uniforms: { ...common },
      side: THREE.DoubleSide,
    });
    this.licMat = new THREE.ShaderMaterial({
      vertexShader: LIC_VS,
      fragmentShader: LIC_FS,
      vertexColors: true,
      side: THREE.DoubleSide,
      uniforms: {
        ...common,
        flowTexture: { value: null as THREE.Texture | null },
        sizeK: { value: 1 },
        passStep: { value: 0.003 },
      },
    });
    // Render-target texture defaults to NoColorSpace in three 0.174, so the encoded
    // direction/noise written in pass 1 is read back unchanged in pass 2.
    this.flowRT = new THREE.WebGLRenderTarget(2, 2, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
    });
  }

  /** World rect (UI coords) covering the full pixel buffer — match the 2D canvas. */
  setCamera(left: number, right: number, bottom: number, top: number): void {
    this.camera.left = left;
    this.camera.right = right;
    this.camera.top = top;
    this.camera.bottom = bottom;
    this.camera.updateProjectionMatrix();
  }

  /** Swap the Mach colormap (e.g. on a light/dark theme flip). */
  setColormap(cm: Colormap): void {
    this.colormap = cm;
  }

  /** Tune the LIC look (streak length / noise frequency) and re-render. */
  setParams(p: { passStep?: number; textureRepeat?: number }): void {
    if (p.passStep !== undefined) this.licMat.uniforms.passStep!.value = p.passStep;
    if (p.textureRepeat !== undefined) {
      this.dirMat.uniforms.textureRepeat!.value = p.textureRepeat;
      this.licMat.uniforms.textureRepeat!.value = p.textureRepeat;
    }
  }

  /**
   * Calibrate the procedural-noise frequency so one noise feature spans a fixed number of
   * DEVICE pixels regardless of zoom or stage size. The noise is `noised(worldPos · textureRepeat)`
   * (chord-space), so a fixed `textureRepeat` puts the feature at a screen size that changes with
   * the world→pixel scale; the old `500×zoom` left it sub-pixel, which the GPU (no mip/AA on a
   * procedural fragment noise) undersampled into a moiré cross-hatch — worse on Retina. Pinning the
   * feature to ~3 device px keeps the streaks fine yet above Nyquist (alias-free) at every zoom.
   * `cssPxPerChord` is the world→CSS-pixel scale of the current (possibly zoomed) view.
   */
  setNoiseForScale(cssPxPerChord: number): void {
    const FEATURE_DEVICE_PX = 3.0; // ≥~2.5 stays above Nyquist (no moiré); smaller = finer streaks
    const tr = Math.max(60, Math.min(40000, (cssPxPerChord * this.pr) / FEATURE_DEVICE_PX));
    this.setParams({ textureRepeat: tr });
  }

  resize(wpx: number, hpx: number): void {
    // Render at the device pixel ratio (capped 2×): the LIC noise is a procedural per-fragment
    // value-noise with no mipmap/anti-aliasing, so rendering at CSS px on a Retina display
    // undersamples it into a moiré cross-hatch. Native-res sampling (here) + the feature-size
    // calibration in `setNoiseForScale` keep the streaks fine and alias-free. The flow render
    // target MUST match the (super-)sampled drawing buffer or pass 1 reintroduces the aliasing.
    const pr = Math.min(typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1, 2);
    if (wpx === this.w && hpx === this.h && pr === this.pr) return;
    this.w = wpx;
    this.h = hpx;
    this.pr = pr;
    this.renderer.setPixelRatio(pr);
    this.renderer.setSize(wpx, hpx, false);
    this.flowRT.setSize(Math.max(2, Math.round(wpx * pr)), Math.max(2, Math.round(hpx * pr)));
    this.licMat.uniforms.sizeK!.value = wpx / hpx;
  }

  setFlow(fm: FlowMesh): void {
    if (this.mesh) {
      this.scene.remove(this.mesh);
      this.mesh.geometry.dispose();
    }
    const n = fm.points.length / 2;
    const pos = new Float32Array(n * 3);
    const cfv = new Float32Array(n * 4);
    const col = new Float32Array(n * 3);
    const nor = new Float32Array(n * 3);
    const [m0, m1] = fm.machRange;
    const dm = m1 - m0 || 1;
    for (let i = 0; i < n; i++) {
      pos[i * 3] = fm.points[i * 2]!;
      pos[i * 3 + 1] = fm.points[i * 2 + 1]!;
      cfv[i * 4] = fm.vel[i * 2]!;
      cfv[i * 4 + 1] = fm.vel[i * 2 + 1]!;
      const [r, g, b] = this.colormap((fm.mach[i]! - m0) / dm);
      col[i * 3] = r;
      col[i * 3 + 1] = g;
      col[i * 3 + 2] = b;
      nor[i * 3 + 2] = 1; // flat slab → +z normal
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    geo.setAttribute("_cfvec", new THREE.BufferAttribute(cfv, 4));
    geo.setAttribute("color", new THREE.BufferAttribute(col, 3));
    geo.setAttribute("normal", new THREE.BufferAttribute(nor, 3));
    geo.setIndex(fm.tris);
    this.mesh = new THREE.Mesh(geo, this.dirMat);
    this.scene.add(this.mesh);
  }

  render(): void {
    if (!this.mesh) {
      this.clear();
      return;
    }
    // pass 1: flow texture
    this.mesh.material = this.dirMat;
    this.renderer.setRenderTarget(this.flowRT);
    this.renderer.clear();
    this.renderer.render(this.scene, this.camera);
    // pass 2: LIC to the canvas
    this.licMat.uniforms.flowTexture!.value = this.flowRT.texture;
    this.mesh.material = this.licMat;
    this.renderer.setRenderTarget(null);
    this.renderer.clear();
    this.renderer.render(this.scene, this.camera);
  }

  clear(): void {
    this.renderer.setRenderTarget(null);
    this.renderer.clear();
  }

  /** Release every GPU resource this view owns. */
  dispose(): void {
    if (this.mesh) {
      this.scene.remove(this.mesh);
      this.mesh.geometry.dispose();
      this.mesh = null;
    }
    this.scene.clear();
    this.dirMat.dispose();
    this.licMat.dispose();
    this.dummyTex.dispose();
    this.flowRT.dispose();
    this.renderer.dispose();
  }
}

export interface FlowVisLayerProps {
  /** RANS slice mesh; `null` renders a clean empty state (no fabricated field). */
  flow: FlowMesh | null;
  width?: number;
  height?: number;
  /**
   * The chord-coordinate view rect to frame, in lockstep with the foil canvas
   * (defaults to `FOIL_VIEW`). When set, the camera reproduces the foil's
   * letterboxed world→pixel mapping EXACTLY, so the field overlays the geometry;
   * the backend clips the slice a little wider than this so the ragged clip edge
   * falls outside the frame. Omit it only to frame a standalone mesh to its own
   * bounds (the legacy bounds-fit fallback).
   */
  view?: ViewRect | null;
}

/**
 * Native LIC flow visualization. When `flow` is null it shows a "Solve to populate
 * the flow field" placeholder — it never fabricates a field. The Three.js view and
 * its render target are torn down on unmount.
 */
export function FlowVisLayer({ flow, width, height, view = FOIL_VIEW }: FlowVisLayerProps) {
  const palette = useHighliftTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const viewRef = useRef<LicView | null>(null);
  const rafRef = useRef<number | null>(null);
  // The last mesh uploaded to the GPU — so zoom/pan (view-only changes) re-render
  // without rebuilding the ~28k-vertex BufferGeometry every frame (smooth panning).
  const lastFlowRef = useRef<FlowMesh | null>(null);

  // Boot the renderer once; tear it down on unmount.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let view: LicView;
    try {
      view = new LicView(canvas);
    } catch {
      // WebGL unavailable — leave the placeholder visible.
      return;
    }
    viewRef.current = view;
    lastFlowRef.current = null; // fresh view owns no geometry yet → force a re-upload
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      view.dispose();
      viewRef.current = null;
      lastFlowRef.current = null;
    };
  }, []);

  // Track the rendered size from explicit props or the container box, and keep the
  // ortho camera framing the slice's bounding box (UI coords) on every change.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const draw = () => {
      const v = viewRef.current;
      if (!v) return;
      // Keep the Mach colormap in step with the live theme so a light/dark flip
      // recolours the streaks on the next draw (this effect re-runs on `palette`).
      v.setColormap(paletteColormap(palette));
      const wpx = Math.max(1, Math.floor(width ?? container.clientWidth));
      const hpx = Math.max(1, Math.floor(height ?? container.clientHeight));
      v.resize(wpx, hpx);
      if (!flow || flow.points.length < 6) {
        v.clear();
        return;
      }
      let left: number;
      let right: number;
      let bottom: number;
      let top: number;
      if (view) {
        // Frame the SHARED foil view so the field maps to the same pixels as the
        // foil canvas (the camera reproduces its letterboxed transform exactly).
        ({ left, right, bottom, top } = viewCameraRect(view, wpx, hpx));
      } else {
        // Standalone fallback: frame the slice to its own bounds with a small
        // margin, then match world aspect to pixel aspect so streaks aren't squashed.
        let minX = Infinity;
        let maxX = -Infinity;
        let minY = Infinity;
        let maxY = -Infinity;
        for (let i = 0; i < flow.points.length; i += 2) {
          const x = flow.points[i]!;
          const y = flow.points[i + 1]!;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
        const padX = (maxX - minX) * 0.05 || 0.5;
        const padY = (maxY - minY) * 0.05 || 0.5;
        left = minX - padX;
        right = maxX + padX;
        bottom = minY - padY;
        top = maxY + padY;
        const worldAspect = (right - left) / (top - bottom);
        const pixAspect = wpx / hpx;
        if (worldAspect < pixAspect) {
          const cx = (left + right) / 2;
          const halfW = ((top - bottom) * pixAspect) / 2;
          left = cx - halfW;
          right = cx + halfW;
        } else {
          const cy = (bottom + top) / 2;
          const halfH = (right - left) / pixAspect / 2;
          bottom = cy - halfH;
          top = cy + halfH;
        }
      }
      // Lock the on-screen LIC grain to a fixed device-pixel size by calibrating the noise
      // frequency from the world→pixel scale of the current view (`right−left` chords across
      // `wpx`). This auto-adapts to both zoom and stage size, so the grain neither magnifies
      // into smears when zoomed in nor aliases into a moiré cross-hatch — the old fixed
      // `500×zoom` put the noise below ~1px/feature, which Retina undersampled into the hatch.
      const cssPxPerChord = wpx / (right - left);
      v.setNoiseForScale(cssPxPerChord);
      v.setCamera(left, right, bottom, top);
      // Only rebuild the mesh geometry when the flow itself changed; zoom/pan reuse
      // the uploaded mesh and just re-run the LIC at the new camera/params.
      if (lastFlowRef.current !== flow) {
        v.setFlow(flow);
        lastFlowRef.current = flow;
      }
      v.render();
    };

    // Render after layout settles (rAF) so clientWidth/Height are populated.
    rafRef.current = requestAnimationFrame(draw);

    let observer: ResizeObserver | null = null;
    if (width === undefined || height === undefined) {
      observer = new ResizeObserver(() => {
        if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(draw);
      });
      observer.observe(container);
    }

    return () => {
      observer?.disconnect();
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
    // `palette` is a dep so a theme flip re-runs draw() and recolours the streaks.
  }, [flow, width, height, palette, view]);

  const hasFlow = !!flow && flow.points.length >= 6;

  // Theme-aware backdrop: a soft elevated centre fading to the panel surface, so
  // the empty-state and the LIC canvas read on both the light and dark page.
  const backdrop = `radial-gradient(circle at 50% 42%, ${palette.surface2}, ${palette.surface} 70%)`;

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: width !== undefined ? width : "100%",
        height: height !== undefined ? height : "100%",
        minHeight: 0,
        overflow: "hidden",
        background: backdrop,
        borderRadius: 8,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          display: hasFlow ? "block" : "none",
        }}
      />
      {!hasFlow ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            color: palette.ink3,
            fontSize: 13,
            letterSpacing: 0.2,
            textAlign: "center",
            padding: 16,
            pointerEvents: "none",
          }}
        >
          Solve to populate the flow field
        </div>
      ) : null}
    </div>
  );
}
